import { ReactNode, useState } from "react";
import dayjs from "dayjs";
import {
  Avatar,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Box,
} from "@mui/material";
import { green, grey, indigo, red } from "@mui/material/colors";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import LoadingButton from "@mui/lab/LoadingButton";
import Typography from "@mui/material/Typography";
import { BalancePlan, BalancePlanStatus } from "api/models/BalancePlan";
import { exportBalancePlan } from "api/resources/balancePlans";
import { downloadXLSX } from "helpers/excelExport";
import ConfirmationModal from "components/Modals/Confirmation";

const iconSize = { width: 16, height: 16 };

const tsoByPriceArea: Record<string, string> = {
  EE: "Elering",
  LT: "Litgrid",
  LV: "AST",
};

const statuses: Record<
  BalancePlanStatus,
  { color: string; icon: ReactNode; tooltip: string }
> = {
  CREATED: {
    color: grey[500],
    icon: <CheckIcon sx={iconSize} />,
    tooltip: "Created",
  },
  SENT: {
    color: indigo[400],
    icon: <ArrowOutwardIcon sx={iconSize} />,
    tooltip: "Sent",
  },
  ACKNOWLEDGED: {
    color: indigo[400],
    icon: <CheckIcon sx={iconSize} />,
    tooltip: "Acknowledged",
  },
  CONFIRMED: {
    color: green[400],
    icon: <CheckIcon sx={iconSize} />,
    tooltip: "Confirmed",
  },
  REJECTED: {
    color: red[400],
    icon: <CloseIcon sx={iconSize} />,
    tooltip: "Rejected",
  },
  FAILED: {
    color: red[400],
    icon: <PriorityHighIcon sx={iconSize} />,
    tooltip: "Failed",
  },
};

export default function BalancePlansTable({
  plans,
  canSend,
  onSend,
}: {
  plans: BalancePlan[];
  canSend: boolean;
  onSend: (plan: BalancePlan) => Promise<void>;
}) {
  const [exporting, setExporting] = useState<number | null>(null);
  const [sending, setSending] = useState<number | null>(null);
  const [planToSend, setPlanToSend] = useState<BalancePlan | null>(null);

  const handleSend = async () => {
    if (!planToSend) return;
    const plan = planToSend;
    setPlanToSend(null);
    setSending(plan.id);
    try {
      await onSend(plan);
    } finally {
      setSending(null);
    }
  };

  const handleExport = async (plan: BalancePlan) => {
    setExporting(plan.id);
    try {
      const data = await exportBalancePlan(plan.id);
      const filename = `Balance_plan_${plan.identification}_${dayjs(plan.targetDate).format("YYYY-MM-DD")}.xlsx`;
      await downloadXLSX(data, filename);
    } catch (error) {
      console.error("Failed to export balance plan", error);
    } finally {
      setExporting(null);
    }
  };

  if (plans.length === 0) {
    return (
      <Typography component="p" sx={{ mt: 1.6 }}>
        No balance plans have been generated for this date yet.
      </Typography>
    );
  }

  return (
    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
      <TableContainer sx={{ maxWidth: 780 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="left">Name</TableCell>
              <TableCell align="center">Date</TableCell>
              <TableCell align="center">Created at</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plans.map((plan) => {
              const status = statuses[plan.status] ?? statuses.CREATED;
              return (
                <TableRow
                  key={plan.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell align="left">{plan.identification}</TableCell>
                  <TableCell align="center">
                    {dayjs(plan.targetDate).format("DD.MM.YYYY")}
                  </TableCell>
                  <TableCell align="center">
                    {dayjs(plan.createdAt).format("DD.MM.YYYY HH:mm")}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip
                      title={
                        plan.statusText ? (
                          <Box>
                            <Box fontWeight="bold">{status.tooltip}</Box>
                            {plan.statusText.split("; ").map((reason) => (
                              <Box key={reason}>{reason}</Box>
                            ))}
                          </Box>
                        ) : (
                          status.tooltip
                        )
                      }
                    >
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          margin: "0 auto",
                          bgcolor: status.color,
                        }}
                      >
                        {status.icon}
                      </Avatar>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <LoadingButton
                      size="small"
                      variant="text"
                      loading={exporting === plan.id}
                      loadingIndicator="Exporting"
                      disabled={exporting === plan.id}
                      onClick={() => handleExport(plan)}
                    >
                      Export
                    </LoadingButton>
                    {canSend && (
                      <LoadingButton
                        size="small"
                        variant="text"
                        loading={sending === plan.id}
                        loadingIndicator="Sending"
                        disabled={sending !== null}
                        onClick={() => setPlanToSend(plan)}
                      >
                        Send
                      </LoadingButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Modal open={planToSend !== null} onClose={() => setPlanToSend(null)}>
        <ConfirmationModal
          title={`Send ${planToSend?.identification} to ${tsoByPriceArea[planToSend?.priceArea ?? ""] ?? "the TSO"}?`}
          subtitle="The plan is submitted to the TSO as a new revision of today's schedule."
          buttonTitle="Send"
          action={handleSend}
          setConfirmationModal={() => setPlanToSend(null)}
        />
      </Modal>
    </Box>
  );
}
